import React, { useState } from 'react';
import { Wallet, Star, Fuel, RefreshCw } from 'lucide-react';
import MiniReviews from '../MiniReviews';
import { CUSTOMER_AVATARS, avatarSrc, avatarThumb } from '../../data/customerAvatars';
import AvatarWithFallback from '../shared/AvatarWithFallback';
import { TANKGUTHABEN_BENEFITS, PREPAID_TANKCARD_BENEFITS } from '../FuelXCardBenefits';
import ProductImageSlider from '../ProductImageSlider';
import { EmbeddedCardBenefits } from '../landing/CardBenefitsShowcase';

const BenefitList = ({ items, accent, testIdPrefix }) => (
  <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2">
    {items.map((b, i) => {
      const Icon = b.icon;
      return (
        <li key={b.title} className="flex items-center gap-2.5" data-testid={`${testIdPrefix}-${i}`}>
          <Icon className={`w-3.5 h-3.5 ${accent} flex-shrink-0`} strokeWidth={2} />
          <span className="text-[12px] text-stone-600 leading-tight">{b.title}</span>
        </li>
      );
    })}
  </ul>
);

/** Linke Konfigurator-Spalte: dunkler Card-Showcase + Vorteile. */
export const ConfiguratorShowcase = ({ cardName, cardImgFront, cardImgBack, business = false, hideAvatar = false }) => {
  // Business: Gold-Hero-Slide zeigen. Private: nach FuelX Card direkt die neuen Editorial-Bilder.
  const fuelHeroSrc = null;
  const [flipped, setFlipped] = useState(false);
  const [interacted, setInteracted] = useState(false);
  return (
  <div className="flex flex-col lg:border-r border-stone-200">
    {/* Dunkler Showcase-Container für die Card */}
    <div className="relative bg-stone-950 p-7 sm:p-9 overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_rgba(62,255,126,0.10),_transparent_55%)]" aria-hidden="true" />

      <div className="relative mb-4">
        <p className="text-[10px] font-medium uppercase tracking-[0.24em] text-emerald-300 mb-2.5 flex items-center gap-1.5">
          <Wallet className="w-3 h-3" strokeWidth={2.2} /> Sofort gutgeschrieben
        </p>
        <h3 className="font-display text-white text-4xl sm:text-[2.75rem] leading-[0.95] tracking-[-0.025em]">
          0,1<span className="text-emerald-400"> Punkte/km</span>
        </h3>
        <p className="text-[13px] text-stone-400 mt-2.5 leading-snug max-w-[300px]">
          FuelGood Guthaben mit deiner Card-Bestellung — egal ob Diesel, Benzin oder Elektro.
        </p>
        <p className="inline-flex items-center gap-1.5 mt-3 px-3 py-1.5 rounded-full bg-emerald-400/10 border border-emerald-400/25 text-[11px] font-medium text-emerald-300" data-testid="configurator-liter-badge">
          <Fuel className="w-3 h-3" strokeWidth={2.2} /> Alles fürs Auto. Alles in FuelX.
        </p>
      </div>

      {/* Produktbilder-Slider: Card (mit Flip) + USP-Bilder + Thumbnails */}
      <div className="relative -mx-4 sm:mx-auto sm:max-w-[900px]">
        <ProductImageSlider
          testIdPrefix="configurator-product-slider"
          className="rounded-2xl"
          aspectClass="aspect-square"
          business={business}
          cardSrc={cardImgFront}
          cardAlt={`${cardName} Vorder- und Rückseite`}
          secondSrc={fuelHeroSrc}
          secondAlt={`${cardName} · durchschnittlich 0,40 €/Liter — Benzin, Diesel & Elektro`}
          showThumbnails
          firstSlide={
            <div className="relative w-full h-full flex items-center justify-center px-2">
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setInteracted(true); setFlipped((v) => !v); }}
                className="relative block w-full group cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400/60 focus-visible:ring-offset-2 focus-visible:ring-offset-stone-950 rounded-2xl"
                style={{ perspective: '2000px' }}
                aria-label={`${cardName} — Klicken zum Umdrehen`}
                data-testid="configurator-card-flip-button"
              >
                <div className={`relative w-full ${!flipped && !interacted ? 'card-idle-motion' : ''}`} style={{ transformStyle: 'preserve-3d' }}>
                  <div
                    className="relative w-full aspect-[1.586/1] transition-transform duration-700"
                    style={{
                      transformStyle: 'preserve-3d',
                      transform: flipped ? 'rotateY(180deg)' : 'rotateY(0deg)',
                    }}
                  >
                    <img
                      src={cardImgFront}
                      alt={`${cardName} Vorderseite`}
                      className="absolute inset-0 w-full h-full object-contain drop-shadow-[0_35px_60px_rgba(0,0,0,0.75)]"
                      style={{ backfaceVisibility: 'hidden' }}
                      data-testid="configurator-card-image"
                    />
                    <img
                      src={cardImgBack}
                      alt={`${cardName} Rückseite`}
                      className="absolute inset-0 w-full h-full object-contain drop-shadow-[0_35px_60px_rgba(0,0,0,0.75)]"
                      style={{ backfaceVisibility: 'hidden', transform: 'rotateY(180deg)' }}
                      data-testid="configurator-card-back"
                    />
                  </div>
                </div>
              </button>
              <span className="absolute bottom-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/10 backdrop-blur-sm border border-white/15 text-[10px] font-medium tracking-[0.14em] uppercase text-stone-200 pointer-events-none">
                <RefreshCw className="w-3 h-3" strokeWidth={2.4} />
                {flipped ? 'Vorderseite anzeigen' : 'Rückseite anzeigen'}
              </span>
            </div>
          }
        />
      </div>
    </div>

    {/* Vorteile — hell, nur Titel */}
    <div className="flex-1 flex flex-col gap-6 p-7 sm:p-9">
      {/* Trust */}
      <div className="flex items-center gap-3" data-testid="configurator-trust-badge">
        {!hideAvatar && (
          <div className="flex -space-x-1.5 flex-shrink-0">
            {CUSTOMER_AVATARS.slice(0, 5).map((a, i) => (
              <AvatarWithFallback
                key={a.id}
                src={avatarThumb(avatarSrc(a.id), 96)}
                alt={a.alt}
                width="24"
                height="24"
                className="review-photo w-6 h-6 rounded-full border-2 border-white object-cover"
                initialsClassName="text-[8px] font-bold"
                loading="lazy"
                decoding="async"
                data-testid={`trust-avatar-${i}`}
              />
            ))}
          </div>
        )}
        <p className="text-[11px] text-stone-500 flex items-center gap-1.5 flex-wrap">
          <span className="text-stone-900 font-medium">+8.000 Kunden</span>
          <span className="inline-flex items-center gap-0.5" aria-hidden="true">
            {[1, 2, 3, 4, 5].map(i => <Star key={i} className="w-2.5 h-2.5 text-amber-400" fill="currentColor" strokeWidth={0} />)}
          </span>
          4,7 / 5 Kundenbewertung
        </p>
      </div>

      {/* Leistungen — Privat & Business: „Für mehr Auto"-Karten (dunkelgrün, mit Bildern, scrollbar) */}
      <EmbeddedCardBenefits testId="configurator-benefits" />

      {/* Kundenrezension — Story-Slider mit großem Portrait (alle 16 einzigartig, linksbündig) */}
      <div className="text-left">
        <MiniReviews variant="story" limit={16} hideAvatar={hideAvatar} testIdPrefix="configurator-reviews" />
      </div>
    </div>
  </div>
  );
};
